import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';

import type { AiSuggestionsEntryPoint } from '@atlaskit/editor-common/analytics/ai-suggestions-regeneration';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';

import type { AiSuggestionsPlugin } from '../../aiSuggestionsPluginType';
import { fireEntryPointExposureAnalytics } from '../utils/analytics';

export type EntryPointExposureSession = {
	hasBeenExposed: (entryPoint: AiSuggestionsEntryPoint) => boolean;
	markExposed: (entryPoint: AiSuggestionsEntryPoint) => void;
};

// Keep exposure state outside the button so remounts/layout changes do not overcount analytics.
export const createEntryPointExposureSession = (): EntryPointExposureSession => {
	const exposedEntryPoints = new Set<AiSuggestionsEntryPoint>();

	return {
		hasBeenExposed: (entryPoint) => exposedEntryPoints.has(entryPoint),
		markExposed: (entryPoint) => {
			exposedEntryPoints.add(entryPoint);
		},
	};
};

/**
 * Fires entry-point exposure once per toolbar session when the button is visually visible.
 * The toolbar can remount responsive variants, so mount alone can overcount.
 */
export const useEntryPointExposureAnalytics = ({
	api,
	entryPoint,
	exposureSession,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	entryPoint: AiSuggestionsEntryPoint;
	exposureSession: EntryPointExposureSession;
}): MutableRefObject<HTMLButtonElement | null> => {
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const hasFiredRef = useRef(false);

	useEffect(() => {
		const element = buttonRef.current;

		if (!element || hasFiredRef.current || exposureSession.hasBeenExposed(entryPoint)) {
			return;
		}

		const fireIfVisible = () => {
			if (hasFiredRef.current || exposureSession.hasBeenExposed(entryPoint)) {
				return;
			}

			const style = window.getComputedStyle(element);
			const isVisible =
				style.display !== 'none' &&
				style.visibility !== 'hidden' &&
				element.getClientRects().length > 0;

			if (isVisible) {
				fireEntryPointExposureAnalytics(api?.analytics?.actions, entryPoint);
				hasFiredRef.current = true;
				exposureSession.markExposed(entryPoint);
			}
		};

		// In tests or older browsers, wait one frame so layout/CSS can settle before checking visibility.
		if (typeof window.IntersectionObserver === 'undefined') {
			const frame = window.requestAnimationFrame(fireIfVisible);
			return () => window.cancelAnimationFrame(frame);
		}

		// Watch for the button becoming visible after responsive/container-query layout resolves.
		const observer = new window.IntersectionObserver(([entry]) => {
			if (entry?.isIntersecting) {
				fireIfVisible();
				observer.disconnect();
			}
		});

		observer.observe(element);

		return () => observer.disconnect();
	}, [api, entryPoint, exposureSession]);

	return buttonRef;
};
